// Excel export for the Portfolio Lab Compare view.
//
// Produces a multi-sheet workbook covering EVERY Compare tab — including data
// that is only ever shown in charts (stochastic fan, box plot, returns
// histogram, backtest time series). Each section is guarded by data presence and
// wrapped so a single failure can't abort the whole export.
//
// Value-scaling conventions (taken from the on-screen renderers, which are the
// source of truth):
//   • SAC / lookthrough weights / analytics %-metrics / box-plot / factor risks /
//     vol weight+contribution / TE + TE contribution / stress returns
//                                                           → decimals, ×100 here
//   • ratios (return/risk, sortino, calmar), expected neg years, factor betas,
//     factor weight, R², tilt (already pp), contribution_pct (already %),
//     backtest metrics & series (already %)                 → used as-is
//   • holdings weights and fund-level sleeve TE are ALREADY in percentage points
//     (the comparison table / backend emit them as %)       → 1 dp / 2 dp as-is
import XLSX from 'xlsx-js-style';

const HDR = {
  font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 10 },
  fill: { fgColor: { rgb: '072040' } },
  alignment: { horizontal: 'center' },
};
const TOTAL = { font: { bold: true, color: { rgb: '072040' }, sz: 10 }, fill: { fgColor: { rgb: 'F0A500' } } };

const r2 = (v) => (v == null || !Number.isFinite(+v) ? '' : +(+v).toFixed(2));
const r3 = (v) => (v == null || !Number.isFinite(+v) ? '' : +(+v).toFixed(3));
// Pass-through to 1 dp — for values that are ALREADY in percentage points
// (e.g. holdings weights), which must not be re-multiplied by 100.
const n1 = (v) => (v == null || !Number.isFinite(+v) ? '' : +(+v).toFixed(1));
const pc1 = (v) => (v == null || !Number.isFinite(+v) ? '' : +(+v * 100).toFixed(1));
const pc2 = (v) => (v == null || !Number.isFinite(+v) ? '' : +(+v * 100).toFixed(2));

// Build a styled worksheet from an array-of-arrays. Row 0 is the navy header;
// any row index in `totalRows` gets the orange total style. Columns auto-size.
function makeSheet(aoa, { totalRows = [], widths } = {}) {
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const ncol = aoa.reduce((m, row) => Math.max(m, row.length), 0);
  ws['!cols'] = Array.from({ length: ncol }, (_, c) => {
    if (widths && widths[c] != null) return { wch: widths[c] };
    let len = 10;
    for (const row of aoa) len = Math.max(len, String(row[c] ?? '').length + 2);
    return { wch: Math.min(46, len) };
  });
  for (let c = 0; c < ncol; c++) {
    const ref = XLSX.utils.encode_cell({ r: 0, c });
    if (ws[ref]) ws[ref].s = HDR;
  }
  for (const rIdx of totalRows) {
    for (let c = 0; c < ncol; c++) {
      const ref = XLSX.utils.encode_cell({ r: rIdx, c });
      if (ws[ref]) ws[ref].s = TOTAL;
    }
  }
  return ws;
}

// Excel sheet names: ≤31 chars, unique, no : \ / ? * [ ].
function append(wb, used, name, ws) {
  let base = String(name).replace(/[:\\/?*[\]]/g, ' ').slice(0, 31) || 'Sheet';
  let n = base; let i = 2;
  while (used.has(n)) { const s = ` ${i}`; n = base.slice(0, 31 - s.length) + s; i += 1; }
  used.add(n);
  XLSX.utils.book_append_sheet(wb, ws, n);
}

export function exportComparisonToExcel(payload) {
  const {
    comparisonTable, analyticsData, factorData, backtestData,
    overlays = [], benchmarkKey, benchmarkName,
    saaVintage, scenarioName,
  } = payload;

  const ports = comparisonTable?.portfolios || [];
  if (ports.length === 0) return false;
  const A = (key) => analyticsData?.portfolios?.[key];

  const wb = XLSX.utils.book_new();
  const used = new Set();
  const portCols = ports.map((p) => p.name);

  // ── Overview ──────────────────────────────────────────────────────────────
  try {
    const aoa = [['Parameter', 'Value']];
    aoa.push(['Exported At', new Date().toISOString()]);
    aoa.push(['Portfolios Compared', ports.length]);
    if (benchmarkName) aoa.push(['Benchmark', benchmarkName]);
    if (saaVintage != null) aoa.push(['SAA Vintage', `FY${saaVintage}`]);
    aoa.push(['CMA Scenario', scenarioName || 'Default']);
    aoa.push([]);
    aoa.push(['Portfolio', 'Type']);
    ports.forEach((p) => aoa.push([p.name, p.type]));
    append(wb, used, 'Overview', makeSheet(aoa, { widths: [28, 40] }));
  } catch (e) { console.error('Overview sheet failed:', e); }

  // ── Holdings (fund level) ───────────────────────────────────────────────────
  try {
    const rows = comparisonTable?.rows || [];
    if (rows.length) {
      // Holdings weights are ALREADY in percentage points (the comparison table
      // renders them straight with a % sign — no ×100), so emit them as-is.
      const aoa = [['Asset Class', 'Fund Code', 'Fund Name', ...portCols]];
      rows.forEach((row) => {
        aoa.push([row.assetClass || '', row.code || '', row.name || '',
          ...ports.map((p) => n1(row.weights?.[p.key]))]);
      });
      const totalRow = ['Total', '', '', ...ports.map((p) =>
        n1(rows.reduce((s, row) => s + (row.weights?.[p.key] || 0), 0)))];
      aoa.push(totalRow);
      append(wb, used, 'Holdings', makeSheet(aoa, { totalRows: [aoa.length - 1] }));
    }
  } catch (e) { console.error('Holdings sheet failed:', e); }

  // ── Sub-asset-class weights ─────────────────────────────────────────────────
  try {
    const sacSet = new Set();
    ports.forEach((p) => Object.keys(A(p.key)?.sac_weights || {}).forEach((s) => sacSet.add(s)));
    if (sacSet.size) {
      const sacs = [...sacSet];
      const aoa = [['Sub Asset Class', ...portCols]];
      sacs.forEach((s) => aoa.push([s, ...ports.map((p) => pc1(A(p.key)?.sac_weights?.[s]))]));
      append(wb, used, 'SAC Weights', makeSheet(aoa));
    }
  } catch (e) { console.error('SAC weights sheet failed:', e); }

  // ── Asset-class lookthrough ─────────────────────────────────────────────────
  try {
    const acSet = new Set();
    ports.forEach((p) => Object.keys(A(p.key)?.lookthrough || {}).forEach((a) => acSet.add(a)));
    if (acSet.size) {
      const acs = [...acSet];
      const aoa = [['Asset Class', ...portCols]];
      acs.forEach((a) => aoa.push([a, ...ports.map((p) => pc1(A(p.key)?.lookthrough?.[a]))]));
      append(wb, used, 'Asset Class Lookthrough', makeSheet(aoa));
    }
  } catch (e) { console.error('Lookthrough sheet failed:', e); }

  // ── Analytics metrics ───────────────────────────────────────────────────────
  try {
    if (analyticsData?.portfolios) {
      const M = [
        ['Expected Return (%)', (d) => pc2(d?.expected_return)],
        ['Volatility (%)', (d) => pc2(d?.volatility)],
        ['Growth Exposure (%)', (d) => pc1(d?.growth_exposure)],
        ['Income Yield (%)', (d) => pc2(d?.income)],
        ['Objective', (d) => d?.objective?.objective_label ?? ''],
        ['Objective Rate (%)', (d) => pc2(d?.objective?.objective_rate)],
        ['Exp. vs Objective (%)', (d) => pc2(d?.objective?.excess_vs_expected)],
        ['P(Meet Objective) (%)', (d) => pc1(d?.objective?.objective_probability)],
        ['Return/Risk Ratio', (d) => r3(d?.stochastics?.metrics?.return_risk_ratio)],
        ['Sortino Ratio', (d) => r3(d?.stochastics?.metrics?.sortino_ratio)],
        ['Calmar Ratio', (d) => r3(d?.stochastics?.metrics?.calmar_ratio)],
        ['VaR 95% (%)', (d) => pc2(d?.stochastics?.metrics?.var_95)],
        ['CVaR 95% (%)', (d) => pc2(d?.stochastics?.metrics?.cvar_95)],
        ['Avg Max Drawdown (%)', (d) => pc2(d?.stochastics?.metrics?.avg_max_drawdown)],
        ['Worst Max Drawdown (%)', (d) => pc2(d?.stochastics?.metrics?.worst_max_drawdown)],
        ['Worst 1-Year Return (%)', (d) => pc2(d?.stochastics?.metrics?.worst_1yr)],
        ['Win Rate (%)', (d) => pc1(d?.stochastics?.metrics?.win_rate)],
        ['Expected Neg. Years (20yr)', (d) => r2(d?.stochastics?.metrics?.expected_negative_years)],
        ['SRM Risk Band', (d) => (d?.srm_band != null ? `${d.srm_band} — ${d.srm_label || ''}` : '')],
        ['Funds Matched', (d) => (d ? `${d.matched_count}/${d.total_count}` : '')],
      ];
      const aoa = [['Metric', ...portCols]];
      M.forEach(([label, get]) => aoa.push([label, ...ports.map((p) => get(A(p.key)))]));
      append(wb, used, 'Analytics', makeSheet(aoa, { widths: [26, ...portCols.map(() => 16)] }));
    }
  } catch (e) { console.error('Analytics sheet failed:', e); }

  // ── Stochastic projection fan (chart-only) ──────────────────────────────────
  try {
    const withPaths = ports.filter((p) => A(p.key)?.stochastics?.paths?.years?.length);
    if (withPaths.length) {
      const years = A(withPaths[0].key).stochastics.paths.years;
      const pcts = ['p5', 'p10', 'p50', 'p90', 'p95'];
      const header = ['Year'];
      withPaths.forEach((p) => pcts.forEach((q) => header.push(`${p.name} ${q.toUpperCase()}`)));
      const aoa = [header];
      years.forEach((yr, i) => {
        const row = [yr];
        withPaths.forEach((p) => {
          const paths = A(p.key).stochastics.paths;
          pcts.forEach((q) => row.push(paths[q]?.[i] ?? ''));
        });
        aoa.push(row);
      });
      append(wb, used, 'Stochastic Paths', makeSheet(aoa));
    }
  } catch (e) { console.error('Stochastic paths sheet failed:', e); }

  // ── Annual return distribution / box plot (chart-only) ──────────────────────
  try {
    const withBox = ports.filter((p) => A(p.key)?.box_plot_data?.[0]?.stats);
    if (withBox.length) {
      const aoa = [['Portfolio', 'Min (%)', 'Q1 (%)', 'Median (%)', 'Q3 (%)', 'Max (%)']];
      withBox.forEach((p) => {
        const s = A(p.key).box_plot_data[0].stats;
        aoa.push([p.name, pc2(s.min), pc2(s.q1), pc2(s.median), pc2(s.q3), pc2(s.max)]);
      });
      append(wb, used, 'Return Distribution', makeSheet(aoa));
    }
  } catch (e) { console.error('Box plot sheet failed:', e); }

  // ── Returns histogram (chart-only) ──────────────────────────────────────────
  try {
    const withHist = ports.filter((p) => A(p.key)?.returns_histograms?.[0]?.bins?.length);
    if (withHist.length) {
      const aoa = [['Portfolio', 'Bin Lower (%)', 'Frequency']];
      withHist.forEach((p) => {
        const h = A(p.key).returns_histograms[0];
        (h.bins || []).forEach((b, i) => aoa.push([p.name, pc2(b), h.frequencies?.[i] ?? '']));
      });
      append(wb, used, 'Returns Histogram', makeSheet(aoa));
    }
  } catch (e) { console.error('Returns histogram sheet failed:', e); }

  // ── Risk: volatility contributions ──────────────────────────────────────────
  try {
    const aoa = [['Portfolio', 'Sub Asset Class', 'Weight (%)', 'Vol Contribution (%)', '% of Total Vol']];
    let any = false;
    ports.forEach((p) => {
      (A(p.key)?.vol_contributions || []).forEach((v) => {
        any = true;
        aoa.push([p.name, v.asset_class, pc1(v.weight), pc2(v.contribution), r2(v.contribution_pct)]);
      });
    });
    if (any) append(wb, used, 'Risk - Vol Contribution', makeSheet(aoa));
  } catch (e) { console.error('Vol contribution sheet failed:', e); }

  // ── Risk: tracking error vs benchmark ───────────────────────────────────────
  try {
    if (benchmarkKey) {
      const aoa = [['Portfolio', 'Benchmark', 'Total TE (%)', 'Asset Class', 'Tilt (pp)', 'TE Contribution (%)', '% of Total TE']];
      let any = false;
      ports.filter((p) => p.key !== benchmarkKey).forEach((p) => {
        const te = A(p.key)?.tracking_error_vs?.[benchmarkKey];
        if (!te?.contributions?.length) return;
        te.contributions.forEach((c) => {
          any = true;
          aoa.push([p.name, benchmarkName || '', pc2(te.tracking_error), c.asset_class,
            r2(c.tilt), pc2(c.contribution), r2(c.contribution_pct)]);
        });
      });
      if (any) append(wb, used, 'Risk - Tracking Error', makeSheet(aoa));
    }
  } catch (e) { console.error('Tracking error sheet failed:', e); }

  // ── Risk: fund-level TE by equity sleeve ────────────────────────────────────
  try {
    const aoa = [['Portfolio', 'Sleeve', 'Sleeve TE (%)', 'Fund', '% of Sleeve TE']];
    let any = false;
    ports.forEach((p) => {
      const ft = A(p.key)?.fund_te || {};
      Object.entries(ft).forEach(([sleeve, data]) => {
        if (sleeve === '_missing_funds') return;  // reserved key, not a sleeve
        Object.entries(data.fund_contributions || {}).forEach(([fund, pct]) => {
          any = true;
          // data.te is already a percentage (backend returns √(...)×100), so
          // pass it through — don't re-multiply by 100.
          aoa.push([p.name, sleeve, r2(data.te), fund, r2(pct)]);
        });
      });
    });
    if (any) append(wb, used, 'Risk - Fund TE', makeSheet(aoa));
  } catch (e) { console.error('Fund TE sheet failed:', e); }

  // ── Factor exposures (SAC level) ────────────────────────────────────────────
  try {
    if (factorData?.portfolios) {
      const factorSet = new Set();
      ports.forEach((p) => {
        const st = factorData.portfolios?.[p.key]?.sac_totals || {};
        Object.values(st).forEach((t) => Object.keys(t.betas || {}).forEach((f) => factorSet.add(f)));
      });
      const factors = [...factorSet];
      const aoa = [['Portfolio', 'Sub Asset Class', 'Weight (%)', 'R²',
        'Specific Risk (%)', 'Factor Risk (%)', 'Total Risk (%)', ...factors]];
      let any = false;
      ports.forEach((p) => {
        const st = factorData.portfolios?.[p.key]?.sac_totals || {};
        Object.entries(st).forEach(([sac, t]) => {
          any = true;
          aoa.push([p.name, sac, r2(t.weight), r2(t.r_squared),
            pc1(t.specific_risk_annual), pc1(t.factor_risk_annual), pc1(t.total_risk_annual),
            ...factors.map((f) => r2(t.betas?.[f]))]);
        });
      });
      if (any) append(wb, used, 'Factor Exposures', makeSheet(aoa));

      // Fund-level factor detail
      const faoa = [['Portfolio', 'Sub Asset Class', 'Fund Code', 'Fund Name', 'Weight (%)', 'R²',
        'Specific Risk (%)', 'Factor Risk (%)', 'Total Risk (%)', ...factors]];
      let fany = false;
      ports.forEach((p) => {
        const groups = factorData.portfolios?.[p.key]?.sac_groups || {};
        Object.entries(groups).forEach(([sac, funds]) => {
          (funds || []).forEach((fd) => {
            fany = true;
            faoa.push([p.name, sac, fd.tr_code || '', fd.fund_name || '', r2(fd.weight), r2(fd.r_squared),
              pc1(fd.specific_risk_annual), pc1(fd.factor_risk_annual), pc1(fd.total_risk_annual),
              ...factors.map((f) => r2(fd.betas?.[f]))]);
          });
        });
      });
      if (fany) append(wb, used, 'Factor Detail (Funds)', makeSheet(faoa));
    }
  } catch (e) { console.error('Factor sheets failed:', e); }

  // ── Backtest time series (chart-only) ───────────────────────────────────────
  try {
    const series = [];
    (backtestData?.portfolios || []).forEach((bp) => {
      if (bp.cumulative_series?.length) series.push({ name: bp.name, points: bp.cumulative_series });
    });
    overlays.forEach((o) => {
      if (o.cumulative_series?.length) series.push({ name: `${o.name} (Actual)`, points: o.cumulative_series });
    });
    if (series.length) {
      const dateSet = new Set();
      series.forEach((s) => s.points.forEach((pt) => dateSet.add(pt.date)));
      const dates = [...dateSet].sort();
      const byDate = series.map((s) => {
        const m = {}; s.points.forEach((pt) => { m[pt.date] = pt.value; }); return m;
      });
      const aoa = [['Date', ...series.map((s) => s.name)]];
      dates.forEach((d) => aoa.push([d, ...byDate.map((m) => (d in m ? m[d] : ''))]));
      append(wb, used, 'Backtest Series', makeSheet(aoa));
    }
  } catch (e) { console.error('Backtest series sheet failed:', e); }

  // ── Backtest metrics ────────────────────────────────────────────────────────
  try {
    const cols = [];
    (backtestData?.portfolios || []).forEach((bp) => { if (bp.metrics) cols.push({ name: bp.name, m: bp.metrics }); });
    overlays.forEach((o) => { if (o.metrics) cols.push({ name: `${o.name} (Actual)`, m: o.metrics }); });
    if (cols.length) {
      const rowsDef = [
        ['Total Return (%)', 'total_return'],
        ['Annualised Return (%)', 'annualised_return'],
        ['Volatility (%)', 'volatility'],
        ['Return / Risk', 'sharpe_ratio'],
        ['Max Drawdown (%)', 'max_drawdown'],
      ];
      // Backtest metrics are already expressed in % on screen — pass through.
      const aoa = [['Metric', ...cols.map((c) => c.name)]];
      rowsDef.forEach(([label, key]) => aoa.push([label, ...cols.map((c) => r2(c.m?.[key]))]));
      append(wb, used, 'Backtest Metrics', makeSheet(aoa));

      if (backtestData?.common_start_date) {
        const meta = [['Parameter', 'Value']];
        meta.push(['Common Start', backtestData.common_start_date]);
        meta.push(['Common End', backtestData.common_end_date]);
        meta.push(['Months', backtestData.num_months]);
        append(wb, used, 'Backtest Window', makeSheet(meta, { widths: [24, 24] }));
      }
    }
  } catch (e) { console.error('Backtest metrics sheet failed:', e); }

  // ── Stress tests ────────────────────────────────────────────────────────────
  try {
    const aoa = [['Portfolio', 'Event', 'Start', 'End', 'Portfolio Return (%)']];
    const caoa = [['Portfolio', 'Event', 'Asset Class', 'Contribution (%)']];
    let any = false; let cany = false;
    ports.forEach((p) => {
      (A(p.key)?.stress_test_results || []).forEach((evt) => {
        any = true;
        aoa.push([p.name, evt.event_name, evt.start_date, evt.end_date, pc2(evt.portfolio_return)]);
        (evt.top_contributors || []).forEach((c) => {
          cany = true;
          caoa.push([p.name, evt.event_name, c.asset_class, pc2(c.contribution)]);
        });
      });
    });
    if (any) append(wb, used, 'Stress Tests', makeSheet(aoa));
    if (cany) append(wb, used, 'Stress Contributors', makeSheet(caoa));
  } catch (e) { console.error('Stress test sheets failed:', e); }

  const stamp = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `Portfolio_Lab_Comparison_${stamp}.xlsx`);
  return true;
}
